import { useMemo } from "react";
import { useSelector } from "react-redux";
import type { BundledPackagePrice } from "@b2bportal/core-packages";
import { useI18nContext } from "@hopper-b2b/i18n";
import { useGetPriceDisplay } from "@tenant/ui";
import { AccountSelectors } from "@/modules/account";
import { calculatePerUnitPrice } from "@/modules/packages/utilities/priceCalculations";
import { PackageBaseSelectors, PackageSearchSelectors } from "@/store/packages";
/**
 * Hook to calculate and format pricing for packages, including points/rewards
 * and per-passenger pricing breakdown.
 */
export const usePackagePrice = (price?: BundledPackagePrice) => {
  const { t } = useI18nContext();

  const packageType = useSelector(
    PackageBaseSelectors.getCurrentActivePackageType
  );

  const passengerCount =
    useSelector(PackageSearchSelectors.getSearchFormPassengerCount) || 1;

  const rewardsAccountId = useSelector(AccountSelectors.getRewardsAccountId);
  const { getPriceDisplay } = useGetPriceDisplay();

  const priceData = useMemo(() => {
    if (!price || !price?.totalPrice) {
      return {
        showPointsPricing: false,
        totalPriceNotDiscounted: null,
        text: {
          totalPrice: "",
          perUnitPrice: "",
          perUnitRewards: "",
        },
      };
    }

    const hasTenantSpecific = Boolean(price.tenantSpecific);

    // When tenantSpecific is present, use it for points pricing combined with totalPrice fiat.
    // Otherwise fall back to accountSpecific within totalPrice.
    const effectivePrices = price.tenantSpecific
      ? {
          fiat: price.totalPrice.fiat,
          accountSpecific: price.tenantSpecific.accountSpecific,
        }
      : price.totalPrice;

    // Check if user has a rewards account with points pricing available
    const accountSpecificPrice =
      rewardsAccountId != null
        ? effectivePrices.accountSpecific?.[rewardsAccountId]
        : undefined;

    const showPointsPricing = Boolean(
      accountSpecificPrice && Object.keys(accountSpecificPrice).length > 0
    );

    const { priceText: totalPrice, rewardsText: totalPoints } = getPriceDisplay(
      {
        prices: effectivePrices,
      }
    );

    // Calculate strikethrough price if available (for displaying discounts)
    // Only show if it differs from the current price to avoid redundant display
    const getStrikethroughPrice = (): string | null => {
      if (!price.strikethroughPrice) return null;

      const {
        priceText: strikethroughPriceText,
        rewardsText: strikethroughRewardsText,
      } = getPriceDisplay({
        prices: price.strikethroughPrice,
      });

      const currentDisplay = showPointsPricing ? totalPoints : totalPrice;
      const strikethroughDisplay = showPointsPricing
        ? strikethroughRewardsText
        : strikethroughPriceText;

      return strikethroughDisplay && strikethroughDisplay !== currentDisplay
        ? strikethroughDisplay
        : null;
    };
    const totalPriceNotDiscounted = getStrikethroughPrice();

    const { priceText: perUnitPriceText, rewardsText: perUnitRewardsText } =
      getPriceDisplay({
        prices: calculatePerUnitPrice({
          totalPrice: effectivePrices,
          unitCount: passengerCount,
          rewardsAccountId,
        }),
      });

    // Format total price string based on whether points pricing is shown
    const totalPriceString = showPointsPricing
      ? hasTenantSpecific
        ? t("core-packages.hotelCardPrice.totalWithPointsPlus", {
            points: totalPoints,
            price: totalPrice,
          })
        : t("core-packages.hotelCardPrice.totalWithPoints", {
            points: totalPoints,
            price: totalPrice,
          })
      : t("core-packages.hotelCardPrice.totalWithPrice", {
          price: totalPrice,
        });

    return {
      showPointsPricing,
      hasTenantSpecific,
      totalPriceNotDiscounted,
      text: {
        totalPrice: totalPriceString,
        perUnitPrice: perUnitPriceText,
        perUnitRewards: perUnitRewardsText,
      },
    };
  }, [price, rewardsAccountId, getPriceDisplay, passengerCount, t]);

  return {
    context: {
      ...priceData,
      packageType,
    },
    handlers: {},
  };
};
